// @ts-check

/**
 * @file Shared offline-grant signing-key resolution.
 *
 * The backend signs offline sync grants with these keys and both the frontend
 * replica and the system-test harness verify them, so every side must resolve
 * the same list. Resolving it in one place is the contract: a key that only one
 * side knows makes the client treat its own grants as unverifiable.
 */

/**
 * One HMAC offline-grant signing key.
 * @typedef {object} OfflineGrantSigningKey
 * @property {boolean} [current] Whether this key signs newly issued grants.
 * @property {string} id Key id carried in the signed grant envelope.
 * @property {string} secret HMAC secret.
 */

/**
 * Stable local key shipped for development and test so offline sync works
 * without configuration. Never usable in production: the frontend verifies with
 * the same list, so a production key must come from the deployment secret store.
 */
export const LOCAL_OFFLINE_GRANT_SIGNING_KEY = Object.freeze({
  current: true,
  id: "offline-grant-local-v1",
  secret: "local-dev-offline-grant-secret-not-for-production"
})

/**
 * Resolves the signing keys one environment should use.
 * Configured keys always win. Production without configuration resolves to an
 * empty list so grants are neither issued nor accepted; every other environment
 * falls back to the stable local key.
 * @param {{configured?: OfflineGrantSigningKey[] | null, environment: string}} args Resolution inputs.
 * @returns {OfflineGrantSigningKey[]} Keys for the environment.
 */
export function resolveOfflineGrantSigningKeys({configured, environment}) {
  if (Array.isArray(configured) && configured.length > 0) return configured
  if (environment === "production") return []

  return [LOCAL_OFFLINE_GRANT_SIGNING_KEY]
}
